import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import classnames from 'classnames/bind';
import styles from './Dialog.module.scss';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const cln = classnames.bind(styles);

/**
 * This is a wrapper component for http://reactcommunity.org/react-modal Dialog. It's up to you what will be displayed
 * inside the Dialog, you just need to provide desired content as children.
 */
function Dialog({
  heading,
  children,
  className,
  onClose = () => {},
  containerClassName,
  headingClassName,
  dialogTrigger,
  ...rest
}) {
  const [shouldCloseOnOverlayClick, setShouldCloseOnOverlayClick] = useState(false);
  const classes = cln('dialog', className);
  const overlay = useRef(null);
  const onMouseUp = event => {
    if (overlay.current?.contains(event.target)) {
      setShouldCloseOnOverlayClick(false);
    } else {
      setShouldCloseOnOverlayClick(true);
    }
  };
  const onClick = event => {
    event.stopPropagation();
  };
  const onAfterClose = () => {
    setShouldCloseOnOverlayClick(false);
  };
  const assignRef = ref => {
    overlay.current = ref;
  };
  return /*#__PURE__*/_jsxs("div", {
    className: containerClassName,
    onClick: onClick,
    onMouseUp: onMouseUp,
    children: [dialogTrigger, /*#__PURE__*/_jsxs(ReactModal, {
      ariaHideApp: false // http://reactcommunity.org/react-modal/accessibility/#app-element
      ,
      onRequestClose: onClose,
      className: classes,
      overlayClassName: cln('overlay'),
      bodyOpenClassName: cln('opened'),
      shouldCloseOnOverlayClick: shouldCloseOnOverlayClick,
      onAfterClose: onAfterClose,
      contentRef: assignRef,
      ...rest,
      children: [heading && /*#__PURE__*/_jsx(Section, {
        variant: "darker",
        className: cln('heading', headingClassName),
        children: heading
      }), children]
    })]
  });
}
Dialog.propTypes = {
  /** Custom class name for dialog */
  className: PropTypes.string,
  /** Custom class name for dialog container */
  containerClassName: PropTypes.string,
  /** When provided, dialog heading will be automatically added for you */
  heading: PropTypes.node,
  /** Custom class name for heading */
  headingClassName: PropTypes.string,
  /** Content that should be displayed inside the Dialog. You can use Dialog.Section helper to be 100% compliant with the designs */
  children: PropTypes.node.isRequired,
  /** Function which should be invoked on overlay click or ESC key press */
  onClose: PropTypes.func,
  /** React element which should open a dialog e.g. button */
  dialogTrigger: PropTypes.element
};

/**
 * Helper component for defining sections inside of the Dialog component. Check the Dialog docs for the usage examples.
 */
function Section({
  children,
  variant = 'default',
  className,
  mode
}) {
  const classes = cln(className, 'section', `section--${variant}`, {
    [`section--${mode}`]: !!mode
  });
  return /*#__PURE__*/_jsx("section", {
    className: classes,
    children: children
  });
}
Section.propTypes = {
  /** Predefined mode for section. E.g use flexRight for section containing buttons to align them to the right */
  mode: PropTypes.oneOf(['flexRight']),
  /** Content of the section */
  children: PropTypes.node.isRequired,
  /** Custom class name */
  className: PropTypes.string,
  /** Use one of defined variants for instance to make some sections darker (default = white) */
  variant: PropTypes.oneOf(['darker', 'default'])
};
Dialog.Section = Section;
export default Dialog;
export { Section };